import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Button, Spinner } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { withFormik, Field, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { useAbility } from '@casl/react';
import { FormikInput, Icon, MainLogo, CustomWebcam } from '../../components';
import { requestSignIn } from '../../actions/auth';
import { sendAlert } from '../../actions/utils';
import { resetPasswordRequest, checkIfUsernameExists, verifyTokenRequest } from '../../requests/utils';
import { AbilityContext } from '../../config/abilityContext';
import { updateAbility } from '../../config/ability';
import PasswordChecklist from './PasswordChecklist';
import './style.scss';

const PIN_LEN = 6;

const formatUsernameMessage = (msg, useClass, color, iconName) => {
  if (!msg) return '';
  const body = msg.replace(/^[✓✕]\s*/, '');
  const token = `${useClass} ${color} ${iconName}`;
  if (/success|ok|green|checkmark/i.test(token)) return `✓ ${body}`;
  if (/danger|error|bad|red|close/i.test(token)) return `✕ ${body}`;
  return msg;
};

const PinDots = ({ value }) => (
  <div className="enrollment-pin-preview">
    {Array.from({ length: PIN_LEN }).map((_, i) => (
      <span key={`pin-dot-${i + 1}`} className={`enrollment-pin-dot${i < (value || '').length ? ' filled' : ''}`} />
    ))}
  </div>
);

const EnrollmentUploadRow = ({ title, hint, previewUrl, fileRef, onFileChange, onCameraClick, error, touched }) => (
  <>
    <div className="enrollment-upload-row">
      <div className="enrollment-upload-preview">
        {previewUrl ? <img src={previewUrl} alt="" /> : <Icon icon="camera" height="20px" width="20px" />}
      </div>
      <div className="enrollment-upload-text">
        <div className="main">{title}</div>
        <div className="hint">{hint}</div>
      </div>
      <div className="enrollment-upload-actions">
        <Button size="sm" variant="primary" type="button" onClick={() => fileRef.current && fileRef.current.click()}>
          Examinar
        </Button>
        <Button size="sm" variant="outline-primary" type="button" onClick={onCameraClick}>
          Abrir cámara
        </Button>
      </div>
      <input ref={fileRef} type="file" accept=".png, .jpg, .jpeg" hidden onChange={onFileChange} />
    </div>
    {error && touched && <div className="text-danger mt-1">{error}</div>}
  </>
);

const ResetPassword = props => {
  const {
    onHide,
    submitVariant,
    errors,
    touched,
    currentEmployee,
    signedIn,
    user,
    history,
    location,
    setFieldValue,
    values
  } = props;

  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isDisable, setIsDisable] = useState(true);
  const [icon, setIcon] = useState('people');
  const [color, setColor] = useState('');
  const [useClass, setUseClass] = useState('');
  const [message, setMessage] = useState('');
  const [cameraFor, setCameraFor] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [signPreview, setSignPreview] = useState('');
  const photoInputRef = useRef(null);
  const signInputRef = useRef(null);
  const ability = useAbility(AbilityContext);
  const dispatch = useDispatch();
  const view = new URLSearchParams(location.search).get('view');
  const companyName = new URLSearchParams(location.search).get('company_name');

  const redirect = () => {
    if (signedIn) {
      updateAbility(ability);
      if (user.role === 'admin' && !currentEmployee) {
        history.replace('/admin/companies');
      } else if (user.role === 'dt' && !currentEmployee) {
        history.replace('/dt/welcome');
      } else {
        history.replace('/profile/dashboard');
      }
    }
  };

  const handleChange = (name, data) => {
    const inputValue = data.target.value;
    const cleanValue = inputValue.replace(/\s+/g, '');
    setFieldValue(name, cleanValue);
  };

  const handleInputPassword = e => {
    setNewPassword(e.target.value);
    setFieldValue(e.target.name, e.target.value);
  };

  const handlePinChange = (name, e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, PIN_LEN);
    setFieldValue(name, digits);
  };

  const handleDisableButton = toogle => {
    setIsDisable(!toogle);
  };

  const handleFileChange = (field, setPreview) => e => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFieldValue(field, file);
    setPreview(URL.createObjectURL(file));
    setCameraFor(null);
  };

  const handleCameraCapture = (field, setPreview) => file => {
    setFieldValue(field, file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSuccessUsername = response => {
    const { data } = response;
    setIcon(data.icon);
    setColor(data.color);
    setUseClass(data.useClass);
    setMessage(data.message);
  };

  const handleCheckIfUsernameExists = value => {
    if (value && value.length > 3) {
      checkIfUsernameExists({
        dispatch,
        params: {
          username: value
        },
        successCallback: handleSuccessUsername
      });
    }
  };

  const handleVerifyToken = () => {
    const token = new URLSearchParams(location.search).get('token');
    verifyTokenRequest({
      dispatch,
      params: {
        token
      },
      failureCallback: error => {
        const { response } = error;
        dispatch(sendAlert({ kind: 'error', message: response?.data?.error }));
        history.replace('/login');
      },
      successCallback: () => {}
    });
  };

  useEffect(redirect, [signedIn, user, currentEmployee]);

  useEffect(handleVerifyToken, []);

  const handleFailureRequest = error => {
    const { response } = error;
    dispatch(sendAlert({ kind: 'error', message: response?.data?.message }));
  };

  const handleEnrolled = () => {
    const token = new URLSearchParams(location.search).get('token');
    setTimeout(() => {
      if (view === 'enrolled') {
        resetPasswordRequest({
          dispatch,
          params: {
            token,
            view,
            company_name: companyName
          },
          formData: true,
          failureCallback: error => {
            handleFailureRequest(error, dispatch);
          },
          successCallback: result => {
            dispatch(sendAlert({ kind: 'success', message: result.data.message }));
            history.replace('/login');
          }
        });
      }
    }, 5000);
  };
  useEffect(handleEnrolled, []);

  const usernameHelpText = formatUsernameMessage(message, useClass, color, icon);
  const usernameHelpClass = `enrollment-avail ${useClass || ''}`.trim();

  return (
    <>
      {view === 'enrolled' && (
        <Row className="justify-content-center align-items-center h-100 img-login text-center">
          <Col sm={6} md={4}>
            <MainLogo size="20px" />
            <p>
              <strong style={{ fontSize: 14 }}>Estimado usuario, espere mientras procesamos su enrolamiento</strong>
            </p>
            <Spinner animation="border" variant="primary" />
          </Col>
        </Row>
      )}
      {(view === 'create' || view === 'reset') && (
        <div className="enrollment-page-wrap">
          <div className="enrollment-page">
            <div className="enrollment-intro">
              <MainLogo className="enrollment-intro-logo" />
              <div>
                <h3>Bienvenido a PeopleWork</h3>
                <p>
                  {view === 'create' ? 'Completa tus datos para activar tu cuenta.' : 'Ingresa tu nueva contraseña.'}
                </p>
              </div>
            </div>
            <Form>
              <div className="enrollment-card">
                {view === 'create' && (
                  <div className="enrollment-section">
                    <h3 className="text-uppercase">CREAR USUARIO</h3>
                    <Field name="username">
                      {({ field }) => (
                        <FormikInput
                          {...field}
                          label="Nombre de usuario"
                          inputType="text"
                          placeholder="Indique un nombre de usuario"
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                          onBlur={() => handleCheckIfUsernameExists(values.username.trim())}
                          onChange={data => handleChange(field.name, data)}
                          rightBtn={<Icon icon="people" color={`${color}`} height="20px" width="20px" />}
                          helpText={usernameHelpText}
                          classHelpText={usernameHelpClass}
                        />
                      )}
                    </Field>
                  </div>
                )}
                <div className="enrollment-section">
                  <h3 className="text-uppercase">{view === 'create' ? 'CREAR' : 'RECUPERAR'} CONTRASEÑA</h3>
                  <Field name="password">
                    {({ field }) => (
                      <FormikInput
                        {...field}
                        label="Contraseña"
                        inputType={showPassword ? 'text' : 'password'}
                        placeholder="Contraseña"
                        onChange={e => handleInputPassword(e)}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                        rightBtn={<Icon icon={showPassword ? 'eye-off' : 'eye'} height="20px" width="20px" />}
                        rightBtnClickHandler={() => setShowPassword(!showPassword)}
                      />
                    )}
                  </Field>
                  <Field name="passwordConfirmation">
                    {({ field }) => (
                      <FormikInput
                        {...field}
                        label="Confirmar contraseña"
                        inputType={showPasswordConfirmation ? 'text' : 'password'}
                        placeholder="Confirmar contraseña"
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                        rightBtn={
                          <Icon icon={showPasswordConfirmation ? 'eye-off' : 'eye'} height="20px" width="20px" />
                        }
                        rightBtnClickHandler={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                      />
                    )}
                  </Field>
                  <PasswordChecklist
                    variant="grid"
                    password={newPassword}
                    achieveRequirements={handleDisableButton}
                    user={user}
                  />
                </div>
                {view === 'create' && (
                  <>
                    <div className="enrollment-section">
                      <h3 className="text-uppercase">CREAR PIN DE FIRMA DIGITAL</h3>
                      <p className="enrollment-section-sub">
                        Usarás este PIN de 6 dígitos cada vez que firmes un documento.
                      </p>
                      <Field name="passwordSignature">
                        {({ field }) => (
                          <>
                            <FormikInput
                              {...field}
                              label="PIN"
                              inputType="password"
                              placeholder="PIN"
                              maxLength={PIN_LEN}
                              inputMode="numeric"
                              onChange={e => handlePinChange(field.name, e)}
                              error={getIn(errors, field.name)}
                              touched={getIn(touched, field.name)}
                            />
                            <PinDots value={values.passwordSignature} />
                          </>
                        )}
                      </Field>
                      <Field name="passwordSignatureConfirmation">
                        {({ field }) => (
                          <>
                            <FormikInput
                              {...field}
                              label="Confirmar PIN"
                              inputType="password"
                              placeholder="Confirmar PIN"
                              maxLength={PIN_LEN}
                              inputMode="numeric"
                              onChange={e => handlePinChange(field.name, e)}
                              error={getIn(errors, field.name)}
                              touched={getIn(touched, field.name)}
                            />
                            <PinDots value={values.passwordSignatureConfirmation} />
                          </>
                        )}
                      </Field>
                    </div>
                    <div className="enrollment-section">
                      <h3 className="text-uppercase">
                        FOTO DEL COLABORADOR <small className="enrollment-optional">Opcional</small>
                      </h3>
                      <p className="enrollment-section-sub">
                        Puedes agregar tu foto ahora o más adelante desde tu perfil.
                      </p>
                      <EnrollmentUploadRow
                        title="Adjuntar foto del colaborador"
                        hint="Formato sugerido 400×400px · máximo 5MB"
                        previewUrl={photoPreview}
                        fileRef={photoInputRef}
                        onFileChange={handleFileChange('employeeProfileImage', setPhotoPreview)}
                        onCameraClick={() => setCameraFor(cameraFor === 'photo' ? null : 'photo')}
                        error={getIn(errors, 'employeeProfileImage')}
                        touched={getIn(touched, 'employeeProfileImage')}
                      />
                      {cameraFor === 'photo' && (
                        <div className="mt-3">
                          <CustomWebcam
                            onChange={handleCameraCapture('employeeProfileImage', setPhotoPreview)}
                            error={getIn(errors, 'employeeProfileImage')}
                            touched={getIn(touched, 'employeeProfileImage')}
                          />
                        </div>
                      )}
                    </div>
                    <div className="enrollment-section">
                      <h3 className="text-uppercase">
                        FIRMA DEL COLABORADOR <small className="enrollment-optional">Opcional</small>
                      </h3>
                      <p className="enrollment-section-sub">
                        Puedes registrar tu firma ahora o más adelante desde tu perfil.
                      </p>
                      <EnrollmentUploadRow
                        title="Adjuntar firma digital"
                        hint="Formato sugerido 150×50px · máximo 5MB"
                        previewUrl={signPreview}
                        fileRef={signInputRef}
                        onFileChange={handleFileChange('employeeSignImage', setSignPreview)}
                        onCameraClick={() => setCameraFor(cameraFor === 'sign' ? null : 'sign')}
                        error={getIn(errors, 'employeeSignImage')}
                        touched={getIn(touched, 'employeeSignImage')}
                      />
                      {cameraFor === 'sign' && (
                        <div className="mt-3">
                          <CustomWebcam
                            onChange={handleCameraCapture('employeeSignImage', setSignPreview)}
                            error={getIn(errors, 'employeeSignImage')}
                            touched={getIn(touched, 'employeeSignImage')}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="enrollment-footer">
                <Button variant={submitVariant} size="lg" type="submit" onClick={onHide} disabled={isDisable}>
                  {view === 'create' ? 'CREAR' : 'RECUPERAR'} CONTRASEÑA
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = state => {
  const { ongoingRequest, signedIn, currentEmployee, user } = state.auth;
  return {
    ongoingRequest,
    signedIn,
    currentEmployee,
    user
  };
};

const initialValues = props => {
  const { location } = props;
  const view = new URLSearchParams(location.search).get('view');

  return {
    username: '',
    password: '',
    passwordConfirmation: '',
    passwordSignature: '',
    passwordSignatureConfirmation: '',
    view,
    nationalIdSerial: '',
    employeeProfileImage: null,
    employeeSignImage: null,
    selectedImage: true
  };
};

const validationSchema = Yup.object().shape({
  password: Yup.string().when('view', {
    is: val => val === 'create' || val === 'reset',
    then: Yup.string().required('Este campo es obligatorio')
  }),
  passwordConfirmation: Yup.string().when('view', {
    is: val => val === 'create' || val === 'reset',
    then: Yup.string()
      .required('Este campo es obligatorio')
      .oneOf([Yup.ref('password'), null], 'Las contraseñas no coinciden')
  }),

  username: Yup.string().when('view', {
    is: val => val === 'create',
    then: Yup.string()
      .test('no-spaces', 'No se permiten espacios en blanco', value => !/\s/.test(value))
      .required('Este campo es obligatorio')
      .min(4, 'El nombre de usuario debe ser de al menos 4 caracteres')
      .max(20, 'Máximo 20 caracteres permitidos')
  }),
  passwordSignature: Yup.string().when('view', {
    is: val => val === 'create',
    then: Yup.string()
      .required('Este campo es obligatorio')
      .matches(/^\d{6}$/, 'El PIN debe tener 6 dígitos')
  }),
  passwordSignatureConfirmation: Yup.string().when('view', {
    is: val => val === 'create',
    then: Yup.string()
      .required('Este campo es obligatorio')
      .matches(/^\d{6}$/, 'El PIN debe tener 6 dígitos')
      .oneOf([Yup.ref('passwordSignature'), null], 'Las contraseñas no coinciden')
  }),
  nationalIdSerial: Yup.string().when('view', {
    is: val => val === 'create',
    then: Yup.string()
      .min(8, 'El serial debe tener al menos 9 dígitos')
      .matches(/^[a-zA-Z0-9]*$/, 'Deben ser caracteres alfanuméricos')
  }),
  employeeProfileImage: Yup.mixed().when('view', {
    is: val => val === 'create',
    then: Yup.mixed()
      .nullable()
      .notRequired()
      .test(
        'FILE_SIZE',
        'La imagen cargada excede el tamaño máximo permitido (5 MB).',
        value => !value?.size || (value && value?.size <= 5242880)
      )
      .test(
        'FILE_FORMAT',
        'El archivo cargado tiene un formato no compatible.',
        value => (!value && !value?.type) || (value && ['image/jpg', 'image/png', 'image/jpeg'].includes(value?.type))
      )
      .test(
        'REPLACE_LETTER',
        'El archivo no debe tener la letra Ñ en su nombre',
        value => (!value && typeof (value?.name === 'undefined')) || (value && !value?.name.includes('ñ'))
      )
  }),
  employeeSignImage: Yup.mixed().when('view', {
    is: val => val === 'create',
    then: Yup.mixed()
      .test(
        'FILE_SIZE',
        'La imagen cargada excede el tamaño máximo permitido (5 MB).',
        value => !value?.size || (value && value?.size <= 5242880)
      )
      .test(
        'FILE_FORMAT',
        'El archivo cargado tiene un formato no compatible.',
        value => (!value && !value?.type) || (value && ['image/jpg', 'image/png', 'image/jpeg'].includes(value?.type))
      )
      .test(
        'REPLACE_LETTER',
        'El archivo no debe tener la letra Ñ en su nombre',
        value => (!value && typeof (value?.name === 'undefined')) || (value && !value?.name.includes('ñ'))
      )
  })
});

const handleFailureRequest = (error, dispatch) => {
  const { response } = error;
  dispatch(sendAlert({ kind: 'error', message: response?.data?.message }));
};

const handleSubmit = (values, { props }) => {
  const { dispatch, location } = props;
  const token = new URLSearchParams(location.search).get('token');
  const view = new URLSearchParams(location.search).get('view');

  resetPasswordRequest({
    dispatch,
    params: {
      username: values.username,
      password: values.password,
      password_confirmation: values.passwordConfirmation,
      password_signature: values.passwordSignature,
      password_signature_confirmation: values.passwordSignatureConfirmation,
      employee_national_id_serial: values.nationalIdSerial,
      employee_image: values.employeeProfileImage,
      employee_digitalsign: values.employeeSignImage,
      token,
      view
    },
    formData: true,
    failureCallback: error => {
      handleFailureRequest(error, dispatch);
    },
    successCallback: result => {
      const { user } = result.data;
      const login = user.role === 'basic' ? user.username : user.email;
      dispatch(sendAlert({ kind: 'success', message: result.data.message }));
      dispatch(
        requestSignIn({
          user: {
            login,
            password: values.password
          }
        })
      );
    }
  });
};

export default withRouter(
  connect(mapStateToProps)(
    withFormik({
      mapPropsToValues: props => initialValues(props),
      validationSchema,
      handleSubmit
    })(ResetPassword)
  )
);
